import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Inbox, CheckCircle2, Send, ChevronRight, RefreshCw, Filter, Search } from "lucide-react";

const TABS = [
  { key: "pending_from_me", label: "Pending From Me", icon: Inbox, color: "bg-amber-100 text-amber-700" },
  { key: "requested_by_me", label: "Requested By Me", icon: Send,  color: "bg-blue-100 text-blue-700" },
  { key: "completed",       label: "Completed",       icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
];

const STATUS_PILL = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  pending_admin_approval: "bg-amber-100 text-amber-800 border-amber-200",
  pending_admin: "bg-amber-100 text-amber-800 border-amber-200",
  initiated: "bg-sky-100 text-sky-800 border-sky-200",
  pm_signed_off: "bg-sky-100 text-sky-800 border-sky-200",
  uploaded: "bg-sky-100 text-sky-800 border-sky-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-slate-100 text-slate-800 border-slate-200",
};

export default function ApprovalCenter() {
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState("pending_from_me");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = (tab = activeTab) => {
    setLoading(true);
    api.get("/promoter/approval-center", { params: { tab, search } })
      .then((r) => { setRows(r.data.rows || []); setCount(r.data.count || 0); })
      .catch(() => { setRows([]); setCount(0); })
      .finally(() => setLoading(false));
  };
  const loadSummary = () => api.get("/promoter/approval-center/summary").then((r) => setSummary(r.data || {}));

  useEffect(() => { load(); loadSummary(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(activeTab); /* eslint-disable-next-line */ }, [activeTab]);

  const filtered = typeFilter ? rows.filter((r) => r.type === typeFilter) : rows;
  const typeOptions = Array.from(new Set(rows.map((r) => r.type))).map((t) => ({ value: t, label: rows.find((r) => r.type === t)?.type_label || t }));

  return (
    <div className="space-y-5" data-testid="approval-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Approval Center</h1>
          <p className="text-sm text-slate-500 mt-1">
            All approvals across Sales, Leave, Financial Rules, Targets, Schemes, Bulk Incentive and Termination — in one place.
          </p>
        </div>
        <Button variant="outline" onClick={() => { load(); loadSummary(); }} data-testid="approval-center-refresh">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Tab cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="approval-center-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          const n = summary[t.key];
          return (
            <button key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`text-left p-3 rounded-md border transition flex items-center gap-3 ${isActive ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid={`approval-tab-${t.key}`}>
              <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isActive ? "bg-white text-slate-900" : t.color}`}>
                <Icon size={16} />
              </div>
              <div className="flex-1">
                <div className={`text-xs uppercase tracking-wider font-semibold ${isActive ? "text-slate-300" : "text-slate-500"}`}>{t.label}</div>
                <div className={`text-2xl font-bold ${isActive ? "text-white" : "text-slate-900"}`}>
                  {isActive ? count : (n ?? "—")}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card><CardContent className="p-3 flex items-end gap-2 flex-wrap" data-testid="approval-center-filters">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-slate-600 block">Search</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-3 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && load(activeTab)}
                    placeholder="type / promoter / dealer / period…"
                    className="pl-7 h-9" data-testid="approval-center-search" />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-600 block">Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="border border-slate-200 rounded-md h-9 px-2 text-sm bg-white"
                  data-testid="approval-center-type">
            <option value="">— All types —</option>
            {typeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={() => load(activeTab)} data-testid="approval-center-apply">
          <Filter size={14} className="mr-1" /> Apply
        </Button>
        <Button variant="ghost" onClick={() => { setSearch(""); setTypeFilter(""); load(activeTab); }}
                data-testid="approval-center-clear">Clear</Button>
      </CardContent></Card>

      {/* Rows */}
      <Card><CardContent className="p-0">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center gap-2"
               data-testid="approval-center-empty">
            <CheckCircle2 size={32} className="text-emerald-400" />
            <div>{activeTab === "pending_from_me" ? "Nothing pending from you — you're all caught up!"
                  : activeTab === "requested_by_me" ? "You haven't submitted any approval requests recently."
                  : "No completed approvals in the last 60 days."}</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((row) => (
              <div key={`${row.type}-${row.id}`}
                   onClick={() => row.deep_link && nav(row.deep_link)}
                   className="p-3 flex items-start gap-3 hover:bg-slate-50 cursor-pointer"
                   data-testid={`approval-row-${row.type}-${row.id?.slice(0, 8)}`}>
                <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                  <Inbox size={14} />
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-y-1 gap-x-3">
                  <div className="sm:col-span-2 text-sm text-slate-900 font-medium">{row.summary || row.type_label}</div>
                  <div className="text-xs text-slate-500 sm:text-right">
                    {row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : ""}
                  </div>
                  <div className="text-xs text-slate-500 sm:col-span-2">
                    <span className="uppercase text-[10px] tracking-wider font-semibold">{row.type_label}</span>
                    {row.submitted_by && <span> · by {row.submitted_by}</span>}
                    {row.pending_with && <span> · waiting on <b>{row.pending_with}</b></span>}
                  </div>
                  <div className="sm:text-right">
                    <Badge variant="outline" className={`uppercase text-[10px] tracking-wider font-semibold ${STATUS_PILL[row.status] || "bg-slate-100 text-slate-700"}`}>
                      {(row.status || "").replace(/_/g, " ")}
                    </Badge>
                  </div>
                  {row.rejection_reason && (
                    <div className="sm:col-span-3 text-xs text-rose-700">
                      <span className="font-semibold">Rejected:</span> {row.rejection_reason}
                    </div>
                  )}
                </div>
                <ChevronRight size={16} className="text-slate-300 mt-2 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
